import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllTalentAgencyUsers.graphql';
import { Query, QueryallTalentAgencyUsersArgs } from '../../__generated__/globalTypes';

export const useAllTalentAgencyUsersQuery = (options?: QueryHookOptions<Query, QueryallTalentAgencyUsersArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallTalentAgencyUsersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllTalentAgencyUsersLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallTalentAgencyUsersArgs>
): QueryResult<Query, QueryallTalentAgencyUsersArgs> & {
  getAllTalentAgencyUsers: LazyQueryExecFunction<Query, QueryallTalentAgencyUsersArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllTalentAgencyUsers, result] = useLazyQuery<Query, QueryallTalentAgencyUsersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllTalentAgencyUsers, ...result };
};

export const useAllTalentAgencyUsersPromiseQuery = () => {
  const client = useApolloClient();

  const getAllTalentAgencyUsers = useCallback(
    (options?: Omit<QueryOptions<QueryallTalentAgencyUsersArgs, Query>, 'query'>) =>
      client.query<Query, QueryallTalentAgencyUsersArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllTalentAgencyUsers };
};
// Suspense Query Hooks
export const useAllTalentAgencyUsersSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallTalentAgencyUsersArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallTalentAgencyUsersArgs>(QUERY, {
    ...options
  });

  return result;
};
